/**
 * High School Athletic Leaderboard System
 *
 * Sport-specific metrics and categories for prospect rankings
 */

// ============================================================================
// SPORTS & POSITIONS
// ============================================================================

export const HIGH_SCHOOL_SPORTS = {
  football: {
    label: 'Football',
    positions: [
      { value: 'qb', label: 'Quarterback' },
      { value: 'rb', label: 'Running Back' },
      { value: 'wr', label: 'Wide Receiver' },
      { value: 'te', label: 'Tight End' },
      { value: 'ol', label: 'Offensive Line' },
      { value: 'dl', label: 'Defensive Line' },
      { value: 'lb', label: 'Linebacker' },
      { value: 'cb', label: 'Cornerback' },
      { value: 'safety', label: 'Safety' },
      { value: 'k', label: 'Kicker' },
      { value: 'p', label: 'Punter' },
      { value: 'ath', label: 'Athlete' },
    ],
  },
  basketball: {
    label: 'Basketball',
    positions: [
      { value: 'pg', label: 'Point Guard' },
      { value: 'sg', label: 'Shooting Guard' },
      { value: 'sf', label: 'Small Forward' },
      { value: 'pf', label: 'Power Forward' },
      { value: 'c', label: 'Center' },
      { value: 'combo', label: 'Combo Guard' },
      { value: 'wing', label: 'Wing' },
    ],
  },
  baseball: {
    label: 'Baseball',
    positions: [
      { value: 'rhp', label: 'RH Pitcher' },
      { value: 'lhp', label: 'LH Pitcher' },
      { value: 'c', label: 'Catcher' },
      { value: '1b', label: 'First Base' },
      { value: '2b', label: 'Second Base' },
      { value: 'ss', label: 'Shortstop' },
      { value: '3b', label: 'Third Base' },
      { value: 'of', label: 'Outfield' },
      { value: 'util', label: 'Utility' },
    ],
  },
  softball: {
    label: 'Softball',
    positions: [
      { value: 'p', label: 'Pitcher' },
      { value: 'c', label: 'Catcher' },
      { value: 'if', label: 'Infield' },
      { value: 'of', label: 'Outfield' },
      { value: 'util', label: 'Utility' },
    ],
  },
  soccer: {
    label: 'Soccer',
    positions: [
      { value: 'gk', label: 'Goalkeeper' },
      { value: 'cb', label: 'Center Back' },
      { value: 'fb', label: 'Fullback' },
      { value: 'cdm', label: 'Defensive Mid' },
      { value: 'cm', label: 'Central Mid' },
      { value: 'cam', label: 'Attacking Mid' },
      { value: 'winger', label: 'Winger' },
      { value: 'st', label: 'Striker' },
    ],
  },
  volleyball: {
    label: 'Volleyball',
    positions: [
      { value: 'setter', label: 'Setter' },
      { value: 'oh', label: 'Outside Hitter' },
      { value: 'mb', label: 'Middle Blocker' },
      { value: 'opp', label: 'Opposite' },
      { value: 'libero', label: 'Libero' },
      { value: 'ds', label: 'Defensive Specialist' },
    ],
  },
  track: {
    label: 'Track & Field',
    positions: [
      { value: 'sprints', label: 'Sprints' },
      { value: 'mid_distance', label: 'Mid Distance' },
      { value: 'distance', label: 'Distance' },
      { value: 'hurdles', label: 'Hurdles' },
      { value: 'jumps', label: 'Jumps' },
      { value: 'throws', label: 'Throws' },
      { value: 'multi', label: 'Multi-Event' },
    ],
  },
  swimming: {
    label: 'Swimming',
    positions: [
      { value: 'free', label: 'Freestyle' },
      { value: 'back', label: 'Backstroke' },
      { value: 'breast', label: 'Breaststroke' },
      { value: 'fly', label: 'Butterfly' },
      { value: 'im', label: 'Individual Medley' },
      { value: 'distance', label: 'Distance' },
    ],
  },
  wrestling: {
    label: 'Wrestling',
    positions: [
      { value: '106', label: '106 lbs' },
      { value: '113', label: '113 lbs' },
      { value: '120', label: '120 lbs' },
      { value: '126', label: '126 lbs' },
      { value: '132', label: '132 lbs' },
      { value: '138', label: '138 lbs' },
      { value: '144', label: '144 lbs' },
      { value: '150', label: '150 lbs' },
      { value: '157', label: '157 lbs' },
      { value: '165', label: '165 lbs' },
      { value: '175', label: '175 lbs' },
      { value: '190', label: '190 lbs' },
      { value: '215', label: '215 lbs' },
      { value: '285', label: '285 lbs' },
    ],
  },
  lacrosse: {
    label: 'Lacrosse',
    positions: [
      { value: 'attack', label: 'Attack' },
      { value: 'midfield', label: 'Midfield' },
      { value: 'defense', label: 'Defense' },
      { value: 'goalie', label: 'Goalie' },
      { value: 'fogo', label: 'Faceoff Specialist' },
      { value: 'lsm', label: 'Long Stick Middie' },
    ],
  },
  golf: {
    label: 'Golf',
    positions: [{ value: 'golfer', label: 'Golfer' }],
  },
  tennis: {
    label: 'Tennis',
    positions: [
      { value: 'singles', label: 'Singles' },
      { value: 'doubles', label: 'Doubles' },
    ],
  },
} as const

export type SportKey = keyof typeof HIGH_SCHOOL_SPORTS

// ============================================================================
// ATHLETIC METRICS
// ============================================================================

export type MetricUnit = 'seconds' | 'inches' | 'feet' | 'lbs' | 'mph' | 'reps' | 'points' | 'yards' | 'meters' | 'percent'
export type MetricDirection = 'lower_better' | 'higher_better'

export type MetricDefinition = {
  id: string
  label: string
  shortLabel: string
  unit: MetricUnit
  direction: MetricDirection
  description: string
  // Typical ranges for high school athletes
  typical: {
    min: number
    max: number
    elite: number // Elite threshold
  }
  // Which sports this metric applies to
  sports: SportKey[] | 'all'
  category: 'speed' | 'agility' | 'strength' | 'power' | 'endurance' | 'skill' | 'physical' | 'academic'
}

export const ATHLETIC_METRICS: Record<string, MetricDefinition> = {
  // ========== SPEED METRICS ==========
  forty_yard: {
    id: 'forty_yard',
    label: '40-Yard Dash',
    shortLabel: '40yd',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Time to complete 40 yards from a 3-point stance',
    typical: { min: 4.3, max: 5.5, elite: 4.5 },
    sports: ['football', 'lacrosse', 'soccer'],
    category: 'speed',
  },
  ten_yard_split: {
    id: 'ten_yard_split',
    label: '10-Yard Split',
    shortLabel: '10yd',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'First 10 yards of the 40-yard dash (explosion)',
    typical: { min: 1.4, max: 1.9, elite: 1.55 },
    sports: ['football'],
    category: 'speed',
  },
  sixty_yard: {
    id: 'sixty_yard',
    label: '60-Yard Dash',
    shortLabel: '60yd',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Standard baseball speed test',
    typical: { min: 6.5, max: 8.0, elite: 6.8 },
    sports: ['baseball', 'softball'],
    category: 'speed',
  },
  shuttle_run: {
    id: 'shuttle_run',
    label: 'Pro Agility (5-10-5)',
    shortLabel: '5-10-5',
    unit: 'seconds',
    direction: 'lower_better',
    description: '5 yards, 10 yards back, 5 yards finish',
    typical: { min: 4.0, max: 5.0, elite: 4.2 },
    sports: ['football', 'basketball', 'lacrosse'],
    category: 'agility',
  },
  three_cone: {
    id: 'three_cone',
    label: '3-Cone Drill (L-Drill)',
    shortLabel: '3-Cone',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Agility test around 3 cones in L-shape',
    typical: { min: 6.5, max: 8.0, elite: 7.0 },
    sports: ['football'],
    category: 'agility',
  },
  lane_agility: {
    id: 'lane_agility',
    label: 'Lane Agility',
    shortLabel: 'Lane Ag',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Basketball defensive slides around the key',
    typical: { min: 10.5, max: 13.0, elite: 11.0 },
    sports: ['basketball'],
    category: 'agility',
  },
  three_quarter_sprint: {
    id: 'three_quarter_sprint',
    label: '3/4 Court Sprint',
    shortLabel: '3/4 Court',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Basketball court sprint (75 feet)',
    typical: { min: 3.0, max: 4.0, elite: 3.2 },
    sports: ['basketball'],
    category: 'speed',
  },

  // ========== POWER METRICS ==========
  vertical_jump: {
    id: 'vertical_jump',
    label: 'Vertical Jump',
    shortLabel: 'Vert',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Standing vertical leap height',
    typical: { min: 20, max: 40, elite: 36 },
    sports: 'all',
    category: 'power',
  },
  broad_jump: {
    id: 'broad_jump',
    label: 'Broad Jump',
    shortLabel: 'Broad',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Standing long jump distance',
    typical: { min: 84, max: 130, elite: 120 },
    sports: ['football', 'basketball', 'track'],
    category: 'power',
  },
  standing_reach: {
    id: 'standing_reach',
    label: 'Standing Reach',
    shortLabel: 'Reach',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Height of reach while standing flat-footed',
    typical: { min: 84, max: 120, elite: 108 },
    sports: ['basketball', 'volleyball'],
    category: 'physical',
  },
  max_touch: {
    id: 'max_touch',
    label: 'Max Vertical Touch',
    shortLabel: 'Max Touch',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Highest point touched during vertical jump',
    typical: { min: 100, max: 144, elite: 130 },
    sports: ['basketball', 'volleyball'],
    category: 'power',
  },

  // ========== STRENGTH METRICS ==========
  bench_press: {
    id: 'bench_press',
    label: 'Bench Press (Max)',
    shortLabel: 'Bench',
    unit: 'lbs',
    direction: 'higher_better',
    description: 'One-rep max bench press',
    typical: { min: 135, max: 350, elite: 300 },
    sports: ['football', 'wrestling'],
    category: 'strength',
  },
  bench_reps: {
    id: 'bench_reps',
    label: 'Bench Press (225 Reps)',
    shortLabel: '225 Reps',
    unit: 'reps',
    direction: 'higher_better',
    description: 'Number of 225lb bench press reps',
    typical: { min: 0, max: 35, elite: 25 },
    sports: ['football'],
    category: 'strength',
  },
  squat: {
    id: 'squat',
    label: 'Back Squat (Max)',
    shortLabel: 'Squat',
    unit: 'lbs',
    direction: 'higher_better',
    description: 'One-rep max back squat',
    typical: { min: 185, max: 500, elite: 450 },
    sports: ['football', 'wrestling', 'track'],
    category: 'strength',
  },
  deadlift: {
    id: 'deadlift',
    label: 'Deadlift (Max)',
    shortLabel: 'Dead',
    unit: 'lbs',
    direction: 'higher_better',
    description: 'One-rep max deadlift',
    typical: { min: 225, max: 550, elite: 500 },
    sports: ['football', 'wrestling'],
    category: 'strength',
  },
  power_clean: {
    id: 'power_clean',
    label: 'Power Clean (Max)',
    shortLabel: 'Clean',
    unit: 'lbs',
    direction: 'higher_better',
    description: 'One-rep max power clean',
    typical: { min: 135, max: 350, elite: 300 },
    sports: ['football', 'track'],
    category: 'power',
  },

  // ========== BASEBALL/SOFTBALL SPECIFIC ==========
  exit_velocity: {
    id: 'exit_velocity',
    label: 'Exit Velocity',
    shortLabel: 'Exit Velo',
    unit: 'mph',
    direction: 'higher_better',
    description: 'Ball speed off the bat',
    typical: { min: 70, max: 105, elite: 95 },
    sports: ['baseball', 'softball'],
    category: 'skill',
  },
  throwing_velocity: {
    id: 'throwing_velocity',
    label: 'Throwing Velocity',
    shortLabel: 'Throw Velo',
    unit: 'mph',
    direction: 'higher_better',
    description: 'Infield/outfield throwing speed',
    typical: { min: 70, max: 95, elite: 90 },
    sports: ['baseball', 'softball'],
    category: 'skill',
  },
  pitching_velocity: {
    id: 'pitching_velocity',
    label: 'Pitching Velocity (Fastball)',
    shortLabel: 'FB Velo',
    unit: 'mph',
    direction: 'higher_better',
    description: 'Fastball velocity from the mound',
    typical: { min: 70, max: 98, elite: 92 },
    sports: ['baseball'],
    category: 'skill',
  },
  pop_time: {
    id: 'pop_time',
    label: 'Pop Time',
    shortLabel: 'Pop',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Time from catch to throw reaching 2nd base (catchers)',
    typical: { min: 1.8, max: 2.3, elite: 1.9 },
    sports: ['baseball', 'softball'],
    category: 'skill',
  },

  // ========== ENDURANCE ==========
  mile_run: {
    id: 'mile_run',
    label: 'Mile Run',
    shortLabel: 'Mile',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Time to complete 1 mile (displayed as MM:SS)',
    typical: { min: 240, max: 480, elite: 280 },
    sports: ['soccer', 'lacrosse', 'basketball', 'track'],
    category: 'endurance',
  },
  beep_test: {
    id: 'beep_test',
    label: 'Beep Test (Pacer)',
    shortLabel: 'Beep',
    unit: 'points',
    direction: 'higher_better',
    description: '20m shuttle run levels completed',
    typical: { min: 5, max: 15, elite: 13 },
    sports: ['soccer', 'lacrosse', 'basketball'],
    category: 'endurance',
  },
  two_mile: {
    id: 'two_mile',
    label: '2-Mile Run',
    shortLabel: '2 Mile',
    unit: 'seconds',
    direction: 'lower_better',
    description: 'Time to complete 2 miles',
    typical: { min: 540, max: 1020, elite: 600 },
    sports: ['track', 'soccer'],
    category: 'endurance',
  },

  // ========== PHYSICAL MEASUREMENTS ==========
  height: {
    id: 'height',
    label: 'Height',
    shortLabel: 'Ht',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Standing height in inches',
    typical: { min: 60, max: 84, elite: 78 },
    sports: 'all',
    category: 'physical',
  },
  weight: {
    id: 'weight',
    label: 'Weight',
    shortLabel: 'Wt',
    unit: 'lbs',
    direction: 'higher_better', // Context-dependent
    description: 'Body weight in pounds',
    typical: { min: 120, max: 300, elite: 220 },
    sports: 'all',
    category: 'physical',
  },
  wingspan: {
    id: 'wingspan',
    label: 'Wingspan',
    shortLabel: 'Wing',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Fingertip to fingertip arm span',
    typical: { min: 60, max: 90, elite: 84 },
    sports: ['basketball', 'volleyball', 'swimming'],
    category: 'physical',
  },
  hand_size: {
    id: 'hand_size',
    label: 'Hand Size',
    shortLabel: 'Hand',
    unit: 'inches',
    direction: 'higher_better',
    description: 'Thumb to pinky spread',
    typical: { min: 8, max: 12, elite: 10.5 },
    sports: ['football', 'basketball'],
    category: 'physical',
  },

  // ========== ACADEMIC ==========
  gpa: {
    id: 'gpa',
    label: 'GPA',
    shortLabel: 'GPA',
    unit: 'points',
    direction: 'higher_better',
    description: 'Grade Point Average (4.0 scale)',
    typical: { min: 2.0, max: 4.0, elite: 3.5 },
    sports: 'all',
    category: 'academic',
  },
  sat_score: {
    id: 'sat_score',
    label: 'SAT Score',
    shortLabel: 'SAT',
    unit: 'points',
    direction: 'higher_better',
    description: 'SAT total score (400-1600)',
    typical: { min: 800, max: 1600, elite: 1400 },
    sports: 'all',
    category: 'academic',
  },
  act_score: {
    id: 'act_score',
    label: 'ACT Score',
    shortLabel: 'ACT',
    unit: 'points',
    direction: 'higher_better',
    description: 'ACT composite score (1-36)',
    typical: { min: 15, max: 36, elite: 30 },
    sports: 'all',
    category: 'academic',
  },
}

// ============================================================================
// SPORT-SPECIFIC METRIC SETS
// ============================================================================

export const SPORT_METRICS: Record<SportKey, string[]> = {
  football: [
    'height', 'weight', 'forty_yard', 'ten_yard_split', 'shuttle_run', 'three_cone',
    'vertical_jump', 'broad_jump', 'bench_press', 'squat', 'power_clean', 'hand_size', 'gpa',
  ],
  basketball: [
    'height', 'weight', 'wingspan', 'standing_reach', 'vertical_jump', 'max_touch',
    'lane_agility', 'three_quarter_sprint', 'shuttle_run', 'gpa',
  ],
  baseball: [
    'height', 'weight', 'sixty_yard', 'exit_velocity', 'throwing_velocity',
    'pitching_velocity', 'pop_time', 'vertical_jump', 'gpa',
  ],
  softball: [
    'height', 'weight', 'sixty_yard', 'exit_velocity', 'throwing_velocity', 'pop_time', 'gpa',
  ],
  soccer: [
    'height', 'weight', 'forty_yard', 'beep_test', 'vertical_jump', 'shuttle_run', 'mile_run', 'gpa',
  ],
  volleyball: [
    'height', 'weight', 'wingspan', 'standing_reach', 'vertical_jump', 'max_touch', 'shuttle_run', 'gpa',
  ],
  track: [
    'height', 'weight', 'forty_yard', 'vertical_jump', 'broad_jump', 'mile_run', 'two_mile', 'gpa',
  ],
  swimming: [
    'height', 'weight', 'wingspan', 'vertical_jump', 'gpa',
  ],
  wrestling: [
    'height', 'weight', 'forty_yard', 'vertical_jump', 'bench_press', 'squat', 'deadlift', 'gpa',
  ],
  lacrosse: [
    'height', 'weight', 'forty_yard', 'shuttle_run', 'vertical_jump', 'mile_run', 'beep_test', 'gpa',
  ],
  golf: [
    'height', 'weight', 'gpa',
  ],
  tennis: [
    'height', 'weight', 'shuttle_run', 'vertical_jump', 'gpa',
  ],
}

// ============================================================================
// GRADUATION CLASS YEARS
// ============================================================================

export function getClassYears() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()
  // After June, focus on next year's class
  const baseYear = currentMonth >= 6 ? currentYear + 1 : currentYear

  return [
    { value: baseYear, label: `${baseYear} (Senior)` },
    { value: baseYear + 1, label: `${baseYear + 1} (Junior)` },
    { value: baseYear + 2, label: `${baseYear + 2} (Sophomore)` },
    { value: baseYear + 3, label: `${baseYear + 3} (Freshman)` },
    { value: baseYear + 4, label: `${baseYear + 4} (8th Grade)` },
  ]
}

// ============================================================================
// LEADERBOARD TYPES
// ============================================================================

export type LeaderboardScope = 'platform' | 'locker_room' | 'state' | 'region'

export type LeaderboardEntry = {
  id: string
  prospect_id: number
  metric_id: string
  value: number
  recorded_at: string
  verified: boolean
  source?: 'self' | 'coach' | 'camp' | 'combine' | 'verified'
  notes?: string
}

export type ProspectLeaderboardProfile = {
  prospect_id: number
  name: string
  avatar_url: string | null
  sport: SportKey
  position: string
  class_year: number
  city: string | null
  state: string | null
  school: string | null
  locker_room_id?: string
  metrics: Record<string, LeaderboardEntry>
}

export type LeaderboardRanking = {
  rank: number
  prospect: ProspectLeaderboardProfile
  value: number
  percentile: number
  change?: number // Position change from last period
}

export type LeaderboardFilter = {
  metric_id: string
  sport?: SportKey
  position?: string
  class_year?: number
  state?: string
  scope: LeaderboardScope
  locker_room_id?: string
  limit?: number
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function formatMetricValue(metricId: string, value: number): string {
  const metric = ATHLETIC_METRICS[metricId]
  if (!metric) return String(value)

  switch (metric.unit) {
    case 'seconds':
      // For times over 60 seconds, format as MM:SS
      if (value >= 60) {
        const mins = Math.floor(value / 60)
        const secs = (value % 60).toFixed(1)
        return `${mins}:${secs.padStart(4, '0')}`
      }
      return `${value.toFixed(2)}s`
    case 'inches':
      // Convert to feet-inches for height/reach
      if (['height', 'standing_reach', 'wingspan'].includes(metricId)) {
        const feet = Math.floor(value / 12)
        const inches = value % 12
        return `${feet}'${inches.toFixed(0)}"`
      }
      return `${value.toFixed(1)}"`
    case 'feet':
      return `${value.toFixed(1)} ft`
    case 'lbs':
      return `${value.toFixed(0)} lbs`
    case 'mph':
      return `${value.toFixed(1)} mph`
    case 'reps':
      return `${value.toFixed(0)} reps`
    case 'points':
      if (metricId === 'gpa') return value.toFixed(2)
      return value.toFixed(0)
    default:
      return String(value)
  }
}

export function getMetricPercentile(metricId: string, value: number): number {
  const metric = ATHLETIC_METRICS[metricId]
  if (!metric) return 50

  const { min, max } = metric.typical
  const range = max - min

  if (metric.direction === 'lower_better') {
    // Lower is better (times)
    const percentile = ((max - value) / range) * 100
    return Math.max(0, Math.min(100, percentile))
  } else {
    // Higher is better
    const percentile = ((value - min) / range) * 100
    return Math.max(0, Math.min(100, percentile))
  }
}

export function isEliteValue(metricId: string, value: number): boolean {
  const metric = ATHLETIC_METRICS[metricId]
  if (!metric) return false

  if (metric.direction === 'lower_better') {
    return value <= metric.typical.elite
  }
  return value >= metric.typical.elite
}

export function getMetricsForSport(sport: SportKey): MetricDefinition[] {
  const metricIds = SPORT_METRICS[sport] || []
  return metricIds.map((id) => ATHLETIC_METRICS[id]).filter(Boolean)
}

export function getPositionsForSport(sport: SportKey) {
  return HIGH_SCHOOL_SPORTS[sport]?.positions || []
}
